import React, { useEffect, useState, useCallback, useRef } from 'react';
import { FaToggleOn, FaToggleOff } from 'react-icons/fa';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { getContractsDetails, updateContract } from '../../../../redux/Slices/ContractSlice';
import axios from 'axios';
import { BASE_URL, getToken } from '../../../../shared/utils.js/utils';
import {
  getContractsCache,
  saveContractsCache,
  generateContractsCacheKey,
  getAllContractsCacheForQuery,
  isOnline,
  getDropdownCache,
  saveDropdownCache,
  generateDropdownCacheKey,
  getAnyDropdownCache
} from '../../../../shared/utils.js/offlineQueue';
import { makeOfflineRequest } from '../../../../shared/utils.js/offlineRequestManager';


const normalizeListResponse = (payload) => {
  if (!payload) return { items: [], pagination: null };

  let items = [];
  let pagination = null;

  if (Array.isArray(payload?.data?.data)) {
    items = payload.data.data;
    pagination = payload.data?.meta || payload.data?.pagination || null;
  } else if (Array.isArray(payload?.data)) {
    items = payload.data;
    pagination = payload.meta || payload.pagination || null;
  } else if (Array.isArray(payload?.items)) {
    items = payload.items;
    pagination = payload.pagination || payload.meta || null;
  } else if (Array.isArray(payload)) {
    items = payload;
    pagination = null;
  } else {
    items = [];
    pagination = payload.pagination || payload.meta || null;
  }

  // Normalize pagination to ensure it has current_page and last_page
  if (pagination) {
    pagination = {
      current_page: pagination.current_page || pagination.page || 1,
      last_page: pagination.last_page || pagination.total_pages || 1,
      per_page: pagination.per_page || 10,
      total: pagination.total || 0,
    };
  }

  return { items, pagination };
};

const EditContract = () => {
  // جميع الحالات الممكنة للعقد
  const CONTRACT_STATUSES = [
    { value: 'draft', labelKey: 'contracts.view.statusValues.draft', defaultValue: 'مسودة' },
    { value: 'active', labelKey: 'contracts.view.statusValues.active', defaultValue: 'مفعل' },
    { value: 'completed', labelKey: 'contracts.view.statusValues.completed', defaultValue: 'مكتمل' },
    { value: 'cancelled', labelKey: 'contracts.view.statusValues.cancelled', defaultValue: 'ملغي' },
    { value: 'inactive', labelKey: 'contracts.view.statusValues.inactive', defaultValue: 'غير نشط' },
  ];
  // تغيير حالة العقد من الدروب داون
  // تحديث الحالة فقط في الفورم، الإرسال عند السمت فقط
  const handleStatusChange = (e) => {
    const newStatus = e.target.value;
    setForm((prev) => ({ ...prev, status: newStatus }));
  };
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [form, setForm] = useState({
    quotation_id: '',
    date: '',
    start_date: '',
    end_date: '',
    retention_percent: '',
    advance_payment_percent: '',
    notes: '',
    status: '',
  });

  // Responsive form width based on sidebar state
  useEffect(() => {
    const updateFormWidth = () => {
      const root = document.documentElement;
      const sidebar = document.querySelector('[data-sidebar]') || document.querySelector('.sidebar') || document.querySelector('[class*="sidebar"]');

      if (sidebar) {
        const isCollapsed = sidebar.classList.contains('collapsed') ||
          sidebar.getAttribute('data-collapsed') === 'true' ||
          window.getComputedStyle(sidebar).width === '0px' ||
          sidebar.offsetWidth < 100;

        if (isCollapsed) {
          root.style.setProperty('--form-max-width', '100%');
        } else {
          root.style.setProperty('--form-max-width', '700px');
        }
      } else {
        const body = document.body;
        const hasSidebar = body.classList.contains('sidebar-open') ||
          body.getAttribute('data-sidebar-open') === 'true';

        if (!hasSidebar) {
          root.style.setProperty('--form-max-width', '100%');
        } else {
          root.style.setProperty('--form-max-width', '700px');
        }
      }
    };

    updateFormWidth();

    const observer = new MutationObserver(updateFormWidth);
    const sidebar = document.querySelector('[data-sidebar]') || document.querySelector('.sidebar') || document.querySelector('[class*="sidebar"]') || document.body;

    if (sidebar) {
      observer.observe(sidebar, {
        attributes: true,
        attributeFilter: ['class', 'data-collapsed'],
        childList: false,
        subtree: false
      });
    }

    window.addEventListener('resize', updateFormWidth);
    window.addEventListener('sidebar-toggle', updateFormWidth);
    document.addEventListener('sidebar-collapsed', updateFormWidth);
    document.addEventListener('sidebar-expanded', updateFormWidth);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateFormWidth);
      window.removeEventListener('sidebar-toggle', updateFormWidth);
      document.removeEventListener('sidebar-collapsed', updateFormWidth);
      document.removeEventListener('sidebar-expanded', updateFormWidth);
    };
  }, []);

  // Quotation dropdown
  const [quotationDropdownOpen, setQuotationDropdownOpen] = useState(false);
  const [quotationSearchTerm, setQuotationSearchTerm] = useState('');
  const [quotationOptions, setQuotationOptions] = useState([]);
  const [quotationPage, setQuotationPage] = useState(1);
  const [quotationPagination, setQuotationPagination] = useState(null);
  const quotationSearchInputRef = useRef(null);

  const fetchQuotations = useCallback(
    async (page = quotationPage, search = quotationSearchTerm) => {
      try {
        const queryParams = { page, per_page: 10, search: search || undefined };
        const cacheKey = generateDropdownCacheKey('quotations', queryParams);

        // Try load from cache first
        const cached = await getDropdownCache(cacheKey);
        if (cached) {
          setQuotationOptions(cached.items || []);
          setQuotationPagination(cached.pagination || null);
        }

        if (isOnline()) {
          const token = getToken();
          if (!token) throw new Error(t('contracts.edit.messages.noAuthToken'));
          const currentLang = i18n.language || 'ar';
          const response = await axios.get(`${BASE_URL}/quotations`, {
            params: queryParams,
            headers: {
              Authorization: `Bearer ${token}`,
              'Accept-Language': currentLang,
            },
          });
          const { items, pagination: paginationMeta } = normalizeListResponse(response.data);

          await saveDropdownCache(cacheKey, items, paginationMeta);
          setQuotationOptions(items);
          setQuotationPagination(paginationMeta);
        } else if (!cached) {
          // Fallback to any cached quotations
          const anyCached = await getAnyDropdownCache('quotations');
          if (anyCached.length > 0) {
            setQuotationOptions(anyCached);
          }
        }
      } catch (error) {
        console.error('Fetch quotations error:', error);
      }
    },
    [t, i18n, isRTL]
  );

  useEffect(() => {
    if (quotationDropdownOpen) {
      fetchQuotations(quotationPage, quotationSearchTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quotationDropdownOpen, quotationPage, quotationSearchTerm]);

  useEffect(() => {
    if (quotationSearchTerm !== undefined && quotationDropdownOpen) {
      const timeoutId = setTimeout(() => {
        setQuotationPage(1);
        fetchQuotations(1, quotationSearchTerm);
      }, 300);
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quotationSearchTerm, quotationDropdownOpen]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const target = event.target;
      const isDropdownContainer = target.closest('[data-dropdown-container]');
      const isDropdownToggle = target.closest('[data-dropdown-toggle]');

      if (!isDropdownContainer && !isDropdownToggle) {
        if (quotationDropdownOpen) setQuotationDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [quotationDropdownOpen]);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        setIsLoading(true);

        // Step 1: try to load from cache always if possible, but prioritize API when online
        let details = null;

        if (!isOnline()) {
          // Find in all cached data
          const allCachedResult = await getAllContractsCacheForQuery();
          details = allCachedResult.items.find(c => String(c.id) === String(id));

          if (details) {
            toast.info(
              isRTL
                ? 'لا يوجد اتصال بالإنترنت. يتم عرض البيانات المحفوظة محلياً.'
                : 'No internet connection. Showing locally saved data.',
              { rtl: isRTL, autoClose: 3000 }
            );
          }
        }

        if (isOnline()) {
          try {
            const payload = await dispatch(getContractsDetails(id)).unwrap();
            details = payload?.data || payload;

            // Save to cache (optimistic - we'd ideally update the specific page cache but let's at least update details)
            // For now just rely on the fact that details will be updated when we go back to list
          } catch (apiError) {
            console.error('[EditContract] API fetch details error:', apiError);
            if (!details) {
              const allCachedResult = await getAllContractsCacheForQuery();
              details = allCachedResult.items.find(c => String(c.id) === String(id));
            }
          }
        }

        if (details) {
          const quotationIdValue = details?.quotation_id ? String(details.quotation_id) : '';

          setForm({
            quotation_id: quotationIdValue,
            date: details?.date || '',
            start_date: details?.start_date || '',
            end_date: details?.end_date || '',
            retention_percent: details?.retention_percent ?? '',
            advance_payment_percent: details?.advance_payment_percent ?? '',
            notes: details?.notes || '',
            status: details?.status || '',
          });
          // تفعيل/إلغاء تفعيل العقد (يجب أن يكون خارج أي useEffect أو شرط)
          const handleToggleStatus = async () => {
            const newStatus = form.status === 'active' ? 0 : 'active';
            try {
              const token = getToken();
              await axios.patch(`${BASE_URL}/contracts/${id}`, { status: newStatus }, {
                headers: { Authorization: `Bearer ${token}` }
              });
              toast.success(
                newStatus === 'active'
                  ? t('contracts.actions.activated', { defaultValue: 'تم تفعيل العقد' })
                  : t('contracts.actions.deactivated', { defaultValue: 'تم إلغاء التفعيل' }),
                { rtl: isRTL }
              );
              setForm((prev) => ({ ...prev, status: newStatus }));
            } catch (error) {
              toast.error(error?.response?.data?.message || t('contracts.actions.toggleError', { defaultValue: 'حدث خطأ أثناء تغيير الحالة' }), { rtl: isRTL });
            }
          };

          // Handle quotation option
          if (details.quotation) {
            setQuotationOptions(prev => {
              const exists = prev.find(q => String(q.id) === String(details.quotation.id));
              return exists ? prev : [...prev, details.quotation];
            });
          } else if (quotationIdValue) {
            // Try to fetch or find in cache
            if (isOnline()) {
              try {
                const token = getToken();
                const currentLang = i18n.language || 'ar';
                const qRes = await axios.get(`${BASE_URL}/quotations/${quotationIdValue}`, {
                  headers: { Authorization: `Bearer ${token}`, 'Accept-Language': currentLang }
                });
                const qData = qRes.data?.data || qRes.data;
                if (qData) {
                  setQuotationOptions(prev => {
                    const exists = prev.find(q => String(q.id) === String(qData.id));
                    return exists ? prev : [...prev, qData];
                  });
                }
              } catch (qErr) {
                console.error('Failed to fetch quotation details:', qErr);
              }
            }
          }
        } else {
          toast.error(t('contracts.edit.messages.fetchError'), { rtl: isRTL });
          navigate('/contracts');
        }
      } catch (error) {
        console.error('General error in fetchDetails:', error);
        toast.error(t('contracts.edit.messages.fetchError'), { rtl: isRTL });
        navigate('/contracts');
      } finally {
        setIsLoading(false);
      }
    };
    fetchDetails();
  }, [dispatch, id, navigate, t, isRTL, i18n.language]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    const contractsData = {
      quotation_id: form.quotation_id || undefined,
      date: form.date || undefined,
      start_date: form.start_date || undefined,
      end_date: form.end_date || undefined,
      retention_percent: form.retention_percent ? Number(form.retention_percent) : undefined,
      advance_payment_percent: form.advance_payment_percent ? Number(form.advance_payment_percent) : undefined,
      notes: form.notes || undefined,
      status: form.status
    };
    try {
      if (!isOnline()) {
        // Update local cache for offline edit
        const allCachedResult = await getAllContractsCacheForQuery();
        let updated = false;
        if (allCachedResult && Array.isArray(allCachedResult.items)) {
          const idx = allCachedResult.items.findIndex(c => String(c.id) === String(id));
          if (idx !== -1) {
            allCachedResult.items[idx] = { ...allCachedResult.items[idx], ...contractsData, id };
            updated = true;
          }
        }
        if (updated) {
          await saveContractsCache(generateContractsCacheKey({ page: 1, per_page: 10, search: '' }), allCachedResult.items, {
            current_page: 1,
            last_page: 1,
            per_page: 10,
            total: allCachedResult.items.length,
          });
        }
        try {
          await makeOfflineRequest(`/contracts/${id}`, 'PUT', contractsData);
        } catch (offlineError) {
          // Only handle the special offline error, ignore it (do not show error toast)
          if (offlineError.message !== 'OFFLINE_REQUEST_SAVED' && !offlineError.isOffline) {
            throw offlineError;
          }
        }
        toast.info(
          isRTL
            ? 'تم حفظ التعديل محلياً وسيتم مزامنته عند عودة الاتصال بالإنترنت'
            : 'Edit saved locally and will sync when online',
          { rtl: isRTL }
        );
        navigate('/contracts');
        return;
      }
      // Online: try redux/online, then update cache if successful
      const resultAction = await dispatch(updateContract({ id, contractsData }));
      if (updateContract.fulfilled.match(resultAction)) {
        // Update cache after successful online edit
        const allCachedResult = await getAllContractsCacheForQuery();
        let updated = false;
        if (allCachedResult && Array.isArray(allCachedResult.items)) {
          const idx = allCachedResult.items.findIndex(c => String(c.id) === String(id));
          if (idx !== -1) {
            allCachedResult.items[idx] = { ...allCachedResult.items[idx], ...contractsData, id };
            updated = true;
          }
        }
        if (updated) {
          await saveContractsCache(generateContractsCacheKey({ page: 1, per_page: 10, search: '' }), allCachedResult.items, {
            current_page: 1,
            last_page: 1,
            per_page: 10,
            total: allCachedResult.items.length,
          });
        }
        toast.success(t('contracts.edit.messages.updateSuccess', { defaultValue: 'تم تحديث العقد بنجاح' }), { rtl: isRTL });
        navigate('/contracts');
      } else {
        toast.dismiss();
        const errorMessage = resultAction.payload || t('contracts.edit.messages.updateError');
        toast.error(errorMessage, { rtl: isRTL });
      }
    } catch (error) {
      const errorData = error?.response?.data || error;
      let errorMessage = t('contracts.edit.messages.updateError', { defaultValue: 'Failed to update contract' });

      if (typeof errorData === 'string') {
        errorMessage = errorData;
      } else if (errorData?.errors) {
        errorMessage = Object.values(errorData.errors).flat().join(', ');
      } else if (errorData?.message) {
        errorMessage = errorData.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage, { rtl: isRTL });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div style={{ ...pageStyle, direction: isRTL ? 'rtl' : 'ltr' }}>
        <div style={{ ...cardStyle, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('contracts.view.loading')}</div>
      </div>
    );
  }

  return (
    <div style={{ ...pageStyle, direction: isRTL ? 'rtl' : 'ltr' }}>
      <div style={{ width: '100%', maxWidth: 'var(--form-max-width, 700px)', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 8, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
          <h2 style={{ marginBottom: 0, fontSize: '20px' }}>{t('contracts.edit.title')}</h2>
          <button type="button" style={{ ...secondaryButtonStyle, backgroundColor: '#666', border: 'none' }} onClick={() => navigate(-1)}>
            {t('common.back', { defaultValue: 'Back' })}
          </button>
        </div>
        <div style={{ ...cardStyle, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
          {/* ...existing code... */}
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ position: 'relative' }} data-dropdown-container>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
                <span style={{ fontSize: 14 }}>{t('contracts.edit.quotation')}</span>
                <div data-dropdown-toggle onClick={() => setQuotationDropdownOpen((prev) => !prev)} style={{ ...dropdownDisplayStyle, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                  <span>
                    {(() => {
                      const selected = getSelectedQuotation(form.quotation_id, quotationOptions);
                      if (!selected) return t('contracts.edit.selectQuotation');
                      const quotationNo = selected.quotation_no || `#${selected.id}`;
                      const subject = selected.subject || '';
                      const project = selected.project ? ` - ${isRTL ? (selected.project.name_ar || selected.project.name_en || '') : (selected.project.name_en || selected.project.name_ar || '')}` : '';
                      return `${quotationNo}${subject ? ` - ${subject}` : ''}${project}`;
                    })()}
                  </span>
                  <span style={{ fontSize: 12 }}>▼</span>
                </div>
                <DropdownWithSearch
                  isOpen={quotationDropdownOpen}
                  searchTerm={quotationSearchTerm}
                  onSearchChange={setQuotationSearchTerm}
                  searchInputRef={quotationSearchInputRef}
                  items={quotationOptions}
                  pagination={quotationPagination}
                  onPageChange={setQuotationPage}
                  onClose={() => setQuotationDropdownOpen(false)}
                  onSelect={(item) => {
                    setForm((prev) => ({ ...prev, quotation_id: String(item.id) }));
                    setQuotationDropdownOpen(false);
                    setQuotationSearchTerm('');
                    setQuotationPage(1);
                  }}
                  label={t('contracts.edit.quotation')}
                  isRTL={isRTL}
                  i18n={i18n}
                />
              </label>
            </div>
            <div style={gridStyle}>
              <Input
                label={t('contracts.edit.currencyName', { defaultValue: 'العملة' })}
                value={getSelectedQuotationCurrencyName(form.quotation_id, quotationOptions)}
                disabled
                isRTL={isRTL}
              />
              <Input
                label={t('contracts.edit.exchangeRate', { defaultValue: 'سعر الصرف' })}
                value={getSelectedQuotationExchangeRate(form.quotation_id, quotationOptions)}
                disabled
                isRTL={isRTL}
              />
            </div>
            <div style={gridStyle}>
              <Input label={t('contracts.edit.date')} type="date" name="date" value={form.date} onChange={handleChange} isRTL={isRTL} />
              <Input
                label={t('contracts.edit.startDate')}
                type="date"
                name="start_date"
                value={form.start_date}
                onChange={handleChange}
                isRTL={isRTL}
              />
              <Input label={t('contracts.edit.endDate')} type="date" name="end_date" value={form.end_date} onChange={handleChange} isRTL={isRTL} />
            </div>
            <div style={gridStyle}>
              <Input
                label={t('contracts.edit.retentionPercent')}
                type="number"
                min="0"
                name="retention_percent"
                value={form.retention_percent}
                onChange={handleChange}
                isRTL={isRTL}
              />
              <Input
                label={t('contracts.edit.advancePaymentPercent')}
                type="number"
                min="0"
                name="advance_payment_percent"
                value={form.advance_payment_percent}
                onChange={handleChange}
                isRTL={isRTL}
              />
            </div>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span>{t('contracts.edit.notes')}</span>
              <textarea
                name="notes"
                value={form.notes}
                onChange={handleChange}
                rows={4}
                style={{
                  padding: 12,
                  borderRadius: 8,
                  border: '1px solid #333',
                  backgroundColor: '#1a1f2e',
                  color: 'white',
                  resize: 'vertical',
                  direction: isRTL ? 'rtl' : 'ltr',
                  textAlign: isRTL ? 'right' : 'left',
                }}
              />
            </label>
            <div style={{ width: '100%', marginTop: 16 }}>
              <label style={{ fontWeight: 'bold', fontSize: 15, marginBottom: 6, display: 'block' }}>
                {t('contracts.edit.status', { defaultValue: i18n.language === 'en' ? 'Contract Status' : 'حالة العقد' })}
              </label>
              <select
                value={form.status}
                onChange={handleStatusChange}
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  borderRadius: 8,
                  border: '1px solid #333',
                  backgroundColor: '#1a1f2e',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: 15,
                  direction: isRTL ? 'rtl' : 'ltr',
                  textAlign: isRTL ? 'right' : 'left',
                  minWidth: 120
                }}
              >
                {CONTRACT_STATUSES.map(status => (
                  <option key={status.value} value={status.value}>
                    {status.value === 'inactive'
                      ? (i18n.language === 'en' ? 'Inactive' : 'غير نشط')
                      : t(status.labelKey, { defaultValue: status.defaultValue })}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexDirection: isRTL ? 'row' : 'row-reverse' }}>
              <button type="submit" style={submitButtonStyle} disabled={isSubmitting}>
                {isSubmitting ? t('contracts.edit.saving') : t('contracts.edit.save')}
              </button>
              <button type="button" style={secondaryButtonStyle} onClick={() => navigate('/contracts')}>
                {t('contracts.edit.cancel')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const Input = ({ label, isRTL = true, ...rest }) => (
  <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
    <span style={{ fontSize: 14 }}>{label}</span>
    <input
      {...rest}
      style={{
        padding: 12,
        borderRadius: 8,
        border: '1px solid #333',
        backgroundColor: '#1a1f2e',
        color: 'white',
        direction: isRTL ? 'rtl' : 'ltr',
        textAlign: isRTL ? 'right' : 'left',
      }}
    />
  </label>
);

const pageStyle = {
  padding: 30,
  backgroundColor: '#14263b',
  minHeight: 'calc(100vh - 80px)',
  color: 'white',
  display: 'flex',
  justifyContent: 'center',
};

const cardStyle = {
  width: '100%',
  maxWidth: 'var(--form-max-width, 700px)',
  backgroundColor: '#1b3350',
  borderRadius: 12,
  padding: 24,
  border: '1px solid #333',
};

const gridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: 16,
};

const submitButtonStyle = {
  backgroundColor: '#AC2000',
  color: 'white',
  border: 'none',
  padding: '12px 18px',
  borderRadius: 8,
  cursor: 'pointer',
  minWidth: 120,
};

const secondaryButtonStyle = {
  backgroundColor: '#666',
  color: 'white',
  border: 'none',
  padding: '10px 18px',
  borderRadius: 8,
  cursor: 'pointer',
  minWidth: 120,
  fontSize: '14px'
};

const getSelectedQuotation = (quotationId, quotationOptions) => {
  if (!quotationId) return null;
  return quotationOptions.find((q) => String(q.id) === String(quotationId));
};

const getSelectedQuotationCurrencyName = (quotationId, quotationOptions) => {
  const selected = getSelectedQuotation(quotationId, quotationOptions);
  if (!selected) return '';
  return (
    selected.currency_name ||
    selected.currency?.name_ar ||
    selected.currency?.name_en ||
    selected.currency?.code ||
    ''
  );
};

const getSelectedQuotationExchangeRate = (quotationId, quotationOptions) => {
  const selected = getSelectedQuotation(quotationId, quotationOptions);
  if (!selected) return '';
  return selected.exchange_rate ?? selected.currency?.exchange_rate ?? '';
};

const dropdownDisplayStyle = {
  width: '100%',
  padding: '12px 14px',
  backgroundColor: '#1a1f2e',
  border: '1px solid #333',
  borderRadius: 8,
  color: 'white',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  cursor: 'pointer',
  textAlign: 'right',
};

const dropdownMenuStyle = {
  position: 'absolute',
  top: 'calc(100% + 4px)',
  left: 0,
  right: 0,
  backgroundColor: '#1a1f2e',
  border: '1px solid #333',
  borderRadius: 8,
  zIndex: 20,
  padding: 12,
  boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
};

const dropdownSearchStyle = {
  width: '100%',
  padding: 10,
  borderRadius: 6,
  border: '1px solid #333',
  backgroundColor: '#14263b',
  color: 'white',
  marginBottom: 10,
};

const dropdownEmptyStyle = {
  padding: 12,
  textAlign: 'center',
  color: '#ccc',
  fontSize: 13,
};

const DropdownWithSearch = ({
  isOpen,
  searchTerm,
  onSearchChange,
  searchInputRef,
  items,
  pagination,
  onPageChange,
  onSelect,
  onClose,
  label,
  isRTL = true,
  i18n,
}) => {
  const { t } = useTranslation();
  const dropdownIsRTL = isRTL !== undefined ? isRTL : (i18n?.language === 'ar');

  useEffect(() => {
    if (isOpen && searchInputRef?.current) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [isOpen, searchTerm, items, searchInputRef]);

  if (!isOpen) return null;

  return (
    <div style={{ ...dropdownMenuStyle, direction: dropdownIsRTL ? 'rtl' : 'ltr' }} onClick={(e) => e.stopPropagation()}>
      <div style={{ padding: 8 }}>
        <input
          ref={searchInputRef}
          type="text"
          placeholder={`${t('contracts.dropdown.searchPlaceholder')} ${label}`}
          value={searchTerm}
          onChange={(e) => {
            onSearchChange(e.target.value);
            setTimeout(() => {
              searchInputRef.current?.focus();
            }, 0);
          }}
          onFocus={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          autoFocus
          style={{ ...dropdownSearchStyle, direction: dropdownIsRTL ? 'rtl' : 'ltr', textAlign: dropdownIsRTL ? 'right' : 'left' }}
        />
      </div>
      {pagination && (pagination.last_page > 1 || (pagination.last_page === 1 && pagination.current_page === 1)) && (
        <div style={{ padding: '8px', borderTop: '1px solid #333', background: '#14263b' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ color: '#ccc', fontSize: 11, direction: dropdownIsRTL ? 'rtl' : 'ltr', textAlign: dropdownIsRTL ? 'right' : 'left' }}>{t('contracts.dropdown.page')} {pagination.current_page} {t('contracts.dropdown.of')} {pagination.last_page}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
            <button type="button" onClick={(e) => { e.stopPropagation(); if (pagination.current_page > 1) onPageChange(1); }} disabled={pagination.current_page === 1} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === 1 ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>««</button>
            <button type="button" onClick={(e) => { e.stopPropagation(); if (pagination.current_page > 1) onPageChange(pagination.current_page - 1); }} disabled={pagination.current_page === 1} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === 1 ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>‹</button>
            {(() => {
              const pages = [];
              const current = pagination.current_page;
              const last = pagination.last_page;
              const showPages = 5;
              let start = Math.max(1, current - Math.floor(showPages / 2));
              let end = Math.min(last, start + showPages - 1);
              if (end - start < showPages - 1) start = Math.max(1, end - showPages + 1);
              for (let i = start; i <= end; i++) pages.push(i);
              return pages.map((page) => (
                <button key={page} type="button" onClick={(e) => { e.stopPropagation(); onPageChange(page); }} style={{ padding: '4px 8px', backgroundColor: page === current ? '#AC2000' : '#1a1f2e', color: 'white', border: page === current ? '1px solid #AC2000' : '1px solid #444', borderRadius: '4px', cursor: 'pointer', fontSize: 10, fontWeight: page === current ? 'bold' : 'normal', minWidth: '28px' }}>{page}</button>
              ));
            })()}
            <button type="button" onClick={(e) => { e.stopPropagation(); if (pagination.current_page < pagination.last_page) onPageChange(pagination.current_page + 1); }} disabled={pagination.current_page === pagination.last_page} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === pagination.last_page ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === pagination.last_page ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === pagination.last_page ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>›</button>
            <button type="button" onClick={(e) => { e.stopPropagation(); if (pagination.current_page < pagination.last_page) onPageChange(pagination.last_page); }} disabled={pagination.current_page === pagination.last_page} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === pagination.last_page ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === pagination.last_page ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === pagination.last_page ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>»»</button>
          </div>
        </div>
      )}
      <div style={{ maxHeight: 300, overflowY: 'auto', paddingBottom: 4 }}>
        {(Array.isArray(items) ? items : []).length === 0 ? (
          <div style={{ ...dropdownEmptyStyle, direction: dropdownIsRTL ? 'rtl' : 'ltr', textAlign: dropdownIsRTL ? 'right' : 'left' }}>{t('contracts.dropdown.noResults')}</div>
        ) : (
          (Array.isArray(items) ? items : []).map((item) => (
            <div
              key={item.id}
              style={{
                padding: '10px 12px',
                borderTop: '1px solid #333',
                cursor: 'pointer',
                direction: dropdownIsRTL ? 'rtl' : 'ltr',
                textAlign: dropdownIsRTL ? 'right' : 'left'
              }}
              onClick={() => {
                onSelect(item);
                onClose?.();
              }}
            >
              {formatQuotation(item, i18n?.language || 'ar')}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const formatQuotation = (quotation, language = 'ar') => {
  if (!quotation) return '—';
  const quotationNo = quotation.quotation_no || (quotation.id ? `#${quotation.id}` : '');
  const subject = quotation.subject || '';
  const isRTL = language === 'ar';
  const project = quotation.project ? ` - ${isRTL ? (quotation.project.name_ar || quotation.project.name_en || '') : (quotation.project.name_en || quotation.project.name_ar || '')}` : '';
  return `${quotationNo}${subject ? ` - ${subject}` : ''}${project}` || `#${quotation.id}`;
};

export default EditContract;

